import React, { useState, useEffect } from 'react';
import { FaArrowLeft } from 'react-icons/fa';
import './AmbassadorForm.css';

function AmbassadorForm() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    socialHandle: '',
    university: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [backUrl, setBackUrl] = useState('/');
  const [phoneTimeout, setPhoneTimeout] = useState(null);

  // Determine appropriate back URL based on referrer
  useEffect(() => {
    const referrer = document.referrer;
    const currentPath = window.location.pathname;
    
    // If they came from the app, go back to app
    if (referrer.includes('/app') || currentPath.startsWith('/app')) {
      setBackUrl('/app');
    }
    // If they came from socials, go back to socials
    else if (referrer.includes('/socials')) {
      setBackUrl('/socials');
    }
    // Default to home page
    else {
      setBackUrl('/');
    }
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (phoneTimeout) {
        clearTimeout(phoneTimeout);
      }
    };
  }, [phoneTimeout]);

  const formatPhoneNumberDisplay = (value) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    const hasPlus = value.startsWith('+');
    
    if (hasPlus && digits.length > 0) {
      // International format: +1 (555) 123-4567
      const digitsWithoutPlus = digits.slice(1); // Remove country code if present
      const countryCode = digits.length > 10 ? digits.slice(0, -10) : '1';
      const localDigits = digits.length > 10 ? digits.slice(-10) : digits;
      
      if (localDigits.length === 0) return `+${countryCode}`;
      if (localDigits.length <= 3) return `+${countryCode} (${localDigits}`;
      if (localDigits.length <= 6) return `+${countryCode} (${localDigits.slice(0, 3)}) ${localDigits.slice(3)}`;
      return `+${countryCode} (${localDigits.slice(0, 3)}) ${localDigits.slice(3, 6)}-${localDigits.slice(6, 10)}`;
    } else {
      // Local format: (555) 123-4567
      if (digits.length === 0) return '';
      if (digits.length <= 3) return `(${digits}`;
      if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Apply character limits and validation
    let newValue = value;
    
    switch (name) {
      case 'firstName':
      case 'lastName':
        // Max 50 characters, letters only
        newValue = value.replace(/[^a-zA-Z\s]/g, '').slice(0, 50);
        break;
      case 'email':
        // Email validation will be done on submit, but clean input
        newValue = value.slice(0, 254); // RFC standard max email length
        break;
      case 'phone':
        // Smart phone number validation with delayed formatting
        newValue = validatePhoneNumber(value);
        
        // Clear existing timeout
        if (phoneTimeout) {
          clearTimeout(phoneTimeout);
        }
        
        // Set new timeout to format phone number after user stops typing
        const newTimeout = setTimeout(() => {
          const formatted = formatPhoneNumberDisplay(newValue);
          if (formatted !== newValue) {
            setFormData(prev => ({ ...prev, phone: formatted }));
          }
        }, 1000); // Format after 1 second of no typing
        
        setPhoneTimeout(newTimeout);
        break;
      case 'socialHandle':
        // Max 30 characters for social handle
        newValue = value.slice(0, 30);
        break;
      case 'university':
        // Max 100 characters for university
        newValue = value.slice(0, 100);
        break;
    }
    
    setFormData({
      ...formData,
      [name]: newValue
    });
  };

  const validatePhoneNumber = (value) => {
    // Remove all non-digit and non-plus characters for counting
    const digitsOnly = value.replace(/\D/g, '');
    const hasPlus = value.startsWith('+');
    
    // If starts with +, allow + followed by max 11 digits (like +1 5551234567)
    if (hasPlus) {
      const plusDigits = value.substring(1).replace(/\D/g, '');
      if (plusDigits.length > 11) {
        // Keep only + and first 11 digits
        const cleanedPlus = '+' + plusDigits.slice(0, 11);
        // Reconstruct with original non-digit characters
        return reconstructPhoneNumber(cleanedPlus, value);
      }
    } else {
      // No plus, allow max 10 digits
      if (digitsOnly.length > 10) {
        // Keep only first 10 digits and reconstruct
        const cleaned = digitsOnly.slice(0, 10);
        return reconstructPhoneNumber(cleaned, value);
      }
    }
    
    return value;
  };

  const reconstructPhoneNumber = (digits, original) => {
    // Reconstruct phone number preserving original formatting characters
    let result = '';
    let digitIndex = 0;
    
    for (let i = 0; i < original.length && digitIndex < digits.length; i++) {
      if (/\d/.test(original[i])) {
        result += digits[digitIndex];
        digitIndex++;
      } else if (original[i] === '+' && digitIndex === 0) {
        result += '+';
      } else if (original[i] === '-' || original[i] === '(' || original[i] === ')' || original[i] === ' ') {
        result += original[i];
      }
    }
    
    return result;
  };

  const validateForm = () => {
    const { firstName, lastName, email, phone } = formData;
    
    // Name validation
    if (firstName.trim().length < 2) {
      setError('First name must be at least 2 characters');
      return false;
    }
    if (lastName.trim().length < 2) {
      setError('Last name must be at least 2 characters');
      return false;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }
    
    // Phone validation - smart validation
    const digitsOnly = phone.replace(/\D/g, '');
    const hasPlus = phone.startsWith('+');
    
    if (hasPlus) {
      const plusDigits = phone.substring(1).replace(/\D/g, '');
      if (plusDigits.length < 10) {
        setError('Please enter a valid phone number (at least 10 digits after +)');
        return false;
      }
    } else {
      if (digitsOnly.length < 10) {
        setError('Please enter a valid phone number (at least 10 digits)');
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Validate form before submission
    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('/.netlify/functions/ambassador-submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      // Check if response is HTML (indicating a routing error)
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('text/html')) {
        throw new Error('Function not found. Please deploy the site again.');
      }

      // Try to parse as JSON, handle if it's not valid JSON
      let result;
      try {
        const text = await response.text();
        result = JSON.parse(text);
      } catch (parseError) {
        throw new Error('Invalid server response. Please try again.');
      }

      if (!response.ok) {
        // Handle specific error messages from the server
        if (result.error) {
          throw new Error(result.error);
        } else {
          throw new Error(`Server error: ${response.status}`);
        }
      }

      setSubmitted(true);
    } catch (error) {
      setError(error.message || 'Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="ambassador-form-container">
        <div className="ambassador-success">
          <img src="/app-icon.png" alt="CrapperMapper App" className="success-icon" />
          <h2>🎯 Application Submitted Successfully!</h2>
          <p>Thank you for your interest in joining our ambassador team! We'll review your application and get back to you soon.</p>
          <div className="success-actions">
            <button 
              className="ambassador-submit-btn"
              onClick={() => window.location.href = backUrl}
            >
              Back to App
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ambassador-form-container">
      <div className="ambassador-form-header">
        <button 
          className="ambassador-back-btn"
          onClick={() => window.location.href = backUrl}
          title="Go back"
        >
          <FaArrowLeft size={20} />
        </button>
        <h1>Join Our Ambassador Team</h1>
        <p>Help us build the ultimate restroom community and get exclusive perks!</p>
      </div>

      <form onSubmit={handleSubmit} className="ambassador-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="firstName">First Name *</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="John"
              required
              disabled={isSubmitting}
              maxLength={50}
            />
          </div>
          <div className="form-group">
            <label htmlFor="lastName">Last Name *</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Doe"
              required
              disabled={isSubmitting}
              maxLength={50}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="email">Email Address *</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="john.doe@example.com"
            required
            disabled={isSubmitting}
            maxLength={254}
          />
        </div>

        <div className="form-group">
          <label htmlFor="phone">Phone Number *</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="555-123-4567"
            required
            disabled={isSubmitting}
          />
        </div>

        <div className="form-group">
          <label htmlFor="socialHandle">Instagram or TikTok Handle</label>
          <input
            type="text"
            id="socialHandle"
            name="socialHandle"
            value={formData.socialHandle}
            onChange={handleChange}
            placeholder="@yourhandle"
            disabled={isSubmitting}
            maxLength={30}
          />
        </div>

        <div className="form-group">
          <label htmlFor="university">University (Optional)</label>
          <input
            type="text"
            id="university"
            name="university"
            value={formData.university}
            onChange={handleChange}
            placeholder="Your university name"
            disabled={isSubmitting}
            maxLength={100}
          />
        </div>

        {error && <div className="form-error">{error}</div>}

        <button 
          type="submit" 
          className="ambassador-submit-btn"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Join Ambassador Team'}
        </button>
      </form>

      <div className="ambassador-form-footer">
        <p>By submitting, you agree to be contacted by our team about ambassador opportunities.</p>
      </div>
    </div>
  );
}

export default AmbassadorForm;
